import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Printer, RotateCcw, ClipboardList, FileDown } from "lucide-react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateBar, todayIST } from "@/components/DateBar";
import { PrintDialog } from "@/components/PrintDialog";

export default function BatchesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || todayIST();
  const [batches, setBatches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [printIds, setPrintIds] = useState(null);

  const load = useCallback(async () => {
    const [b, j] = await Promise.all([api.get("/batches", { params: { date } }), api.get("/print-jobs")]);
    setBatches(b.data);
    setJobs(j.data);
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const processing = batches.some((b) => b.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [processing, load]);

  const printBatch = async (b) => {
    try {
      const { data } = await api.get("/labels/ids", { params: { batch_id: b.id, status: "pending" } });
      if (!data.ids.length) return toast.info("No pending labels in this batch");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const retry = async (b) => {
    try { await api.post(`/batches/${b.id}/retry`); toast.success(`Resuming ${b.file_name} from page ${b.processed + 1}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete batch "${b.file_name}" and its ${b.total_pages} labels?`)) return;
    try { await api.delete(`/batches/${b.id}`); toast.success("Batch deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="batches-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Upload & print history · {date === "all" ? "all dates" : date === todayIST() ? "today" : date}</div>
          <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Batches</h1>
        </div>
        <DateBar value={date} onChange={(d) => { const p = new URLSearchParams(params); p.set("date", d); setParams(p); }} testId="batches-date-bar" />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-800 bg-[#111827]">
        <Table data-testid="batches-table">
          <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">File</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Uploaded</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Pages</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Status</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Matched</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Printed</TableHead>
            <TableHead className="w-96" />
          </TableRow></TableHeader>
          <TableBody>
            {batches.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500" data-testid="batches-empty">No uploads on this date. Try "All dates" or upload from the Dashboard.</TableCell></TableRow>}
            {batches.map((b) => (
              <TableRow key={b.id} className="row-hover border-slate-800/70 hover:bg-slate-800/40" data-testid={`batch-row-${b.id}`}>
                <TableCell><Link to={`/labels?batch=${b.id}&status=all`} className="font-mono text-sm text-blue-300 hover:underline">{b.file_name}</Link></TableCell>
                <TableCell className="text-xs text-slate-400">{new Date(b.created_at).toLocaleString()}<div className="text-slate-500">{b.uploaded_by}</div></TableCell>
                <TableCell className="font-mono">{b.processed} / {b.total_pages}</TableCell>
                <TableCell className="font-mono text-xs uppercase" data-testid={`batch-status-${b.id}`}>
                  {b.status === "done" && <span className="text-emerald-400">done</span>}
                  {b.status === "processing" && <span className={b.stalled ? "text-amber-300" : "text-blue-300"}>{b.stalled ? "stalled" : "processing…"}</span>}
                  {b.status === "error" && <span className="text-red-400" title={b.error}>error<div className="normal-case text-[10px] text-red-300/80">{b.error}</div></span>}
                </TableCell>
                <TableCell className="font-mono"><span className="text-emerald-400">{b.matched}</span>{b.unmatched > 0 && <span className="text-red-400"> / {b.unmatched} unmatched</span>}{b.duplicates > 0 && <div className="text-orange-300" data-testid={`batch-duplicates-${b.id}`}>{b.duplicates} duplicate{b.duplicates > 1 ? "s" : ""} of printed/other labels</div>}</TableCell>
                <TableCell className="font-mono">{b.printed} / {b.total_pages}{b.pending > 0 && <span className="text-amber-300"> · {b.pending} pending</span>}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" disabled={!b.pending || b.status === "processing"} onClick={() => printBatch(b)} data-testid={`batch-print-${b.id}`}><Printer className="mr-1 h-3.5 w-3.5" /> Print {b.pending} pending</Button>
                    <Button asChild size="sm" variant="outline" className="h-8 border-slate-700 bg-transparent hover:bg-slate-800" title="Picklist for this batch"><Link to={`/picklist?batch=${b.id}`} data-testid={`batch-picklist-${b.id}`}><ClipboardList className="mr-1 h-3.5 w-3.5" /> Picklist</Link></Button>
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 bg-transparent hover:bg-slate-800" title="Download batch picklist PDF" data-testid={`batch-picklist-pdf-${b.id}`} disabled={b.status === "processing"}
                      onClick={() => downloadFile(`/picklist/pdf?status=pending&batch_id=${b.id}&date=all`, `picklist-${b.file_name}.pdf`).catch((e) => toast.error(formatApiError(e)))}><FileDown className="h-3.5 w-3.5" /></Button>
                    {(b.status === "error" || b.stalled) && b.file_id && <Button size="sm" variant="outline" className="h-8 border-amber-700 text-amber-300 hover:bg-amber-900/40" onClick={() => retry(b)} data-testid={`batch-retry-${b.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry</Button>}
                    {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-300" onClick={() => remove(b)} data-testid={`batch-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div>
        <h2 className="font-heading mb-3 text-lg font-bold uppercase tracking-tight sm:text-xl">Print jobs</h2>
        <div className="overflow-x-auto rounded-md border border-slate-800 bg-[#111827]">
          <Table data-testid="print-jobs-table">
            <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">When</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">By</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Labels</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {jobs.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-slate-500">No print jobs yet.</TableCell></TableRow>}
              {jobs.map((j) => (
                <TableRow key={j.id} className="border-slate-800/70" data-testid={`print-job-row-${j.id}`}>
                  <TableCell className="text-xs text-slate-400">{new Date(j.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-400">{j.created_by}</TableCell>
                  <TableCell className="font-mono"><Printer className="mr-1 inline h-3.5 w-3.5" />{j.count}</TableCell>
                  <TableCell className={`font-mono text-xs uppercase ${j.status === "completed" ? "text-emerald-400" : "text-amber-300"}`}>{j.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <PrintDialog labelIds={printIds} open={!!printIds} onOpenChange={(o) => !o && setPrintIds(null)} onCompleted={load} onReplaceIds={setPrintIds} />
    </div>
  );
}
